import { useMemo, useContext, useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { Button } from "../../components/button/Button";
import { ModalContext } from "../../components/Modal";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import SearchInput from "../../components/SearchInput";
import {
  sharedUsersForGroupId,
  shareQuerySave,
  shareQueryUsers,
} from "../../redux/shareSlice";
import { showNotification } from "../../redux/notificationSlice";
import Loading from "../../components/Loading";
import {
  CustomNoRowsOverlay,
  noRowsOverlayComponentParams,
} from "../../gridUtils/gridUtils";
import { fetchSavedQueries } from "../../redux/saveSlice";
import LoadingOverlay from "../../components/LoadingOverlay";
import ConfirmPopup from "../../components/ConfirmPopup";
function isModifyList(arr1: any, arr2: any) {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();

  for (let i = 0; i < sorted1.length; i++) {
    if (sorted1[i] !== sorted2[i]) {
      return false;
    }
  }

  return true;
}
const ShareQueryPopup = () => {
  const dispatch = useDispatch<AppDispatch>();
  const modalContext = useContext(ModalContext);
  const close = modalContext ? modalContext.close : () => {};
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmPopUp, setConfirmPopUp] = useState({
    title: "",
    message: "",
    confirmAction: (title: string, id: number) => {},
    closeAction: () => {},
    id: 0,
  });
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [prevSelectedUsers, setPrevSelectedUsers] = useState<any[]>([]);
  const shareUsers = useSelector((state: RootState) => state.share.users);
  const loggedInUser = useSelector((state: RootState) => state.user.user);
  const isLoading = useSelector((state: RootState) => state.share.loading);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const selectedGroupId = useSelector(
    (state: RootState) => state.share.groupId,
  );
  const gridRef = useRef<any>(null);
  const [isSharing, setIsSharing] = useState(false);
  const search = async (val: string) => {
    const lowerCaseVal = val.toLowerCase();
    const filtered = shareUsers
      .filter((user: any) => user.userID !== loggedInUser.name)
      .filter(
        (user: any) =>
          user.userID.toLowerCase().includes(lowerCaseVal) ||
          `${user.firstName} ${user.lastName}`
            .toLowerCase()
            .includes(lowerCaseVal),
      );
    setFilteredUsers(filtered);
  };
  useEffect(() => {
    search("");
  }, []);

  useEffect(() => {
    if (shareUsers.length > 0) {
      const filtered = shareUsers.filter(
        (user: any) => user.userID !== loggedInUser.name,
      );
      setFilteredUsers(filtered);
    }
  }, [shareUsers]);

  useEffect(() => {
    dispatch(shareQueryUsers());
    dispatch(sharedUsersForGroupId(selectedGroupId)).then((response: any) => {
      if (response.meta.requestStatus === "fulfilled") {
        setSelectedUsers(response.payload);
        setPrevSelectedUsers(response.payload);
      }
    });
  }, [dispatch, selectedGroupId]);

  const rowData = useMemo(() => {
    return filteredUsers.map((user: any) => ({
      userId: user.userID,
      userName: `${user.firstName} ${user.lastName}`,
      emailAddress: user.emailAddress,
    }));
  }, [filteredUsers]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      //{ headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 60, filter: false, sortable: false, field: undefined },
      { headerName: "User Name", field: "userName", width: 180 },
      { headerName: "User Email", field: "emailAddress", width: 300 },
    ],
    [],
  );

  const onSelectionChanged = (params: any) => {
    const selectedNodes = params.api.getSelectedNodes();
    const newSelectedIds = selectedNodes.map((node: any) => node.data.userId);

    setSelectedUsers((prevSelectedIds: any[]) => {
      const updatedSelectedIds = new Set(prevSelectedIds);
      // Add newly selected IDs
      newSelectedIds.forEach((id: any) => updatedSelectedIds.add(id));
      // Remove deselected IDs
      params.api.forEachNode((node: any) => {
        if (!newSelectedIds.includes(node.data.userId)) {
          updatedSelectedIds.delete(node.data.userId);
        }
      });
      setErrorMessage(null);
      return Array.from(updatedSelectedIds);
    });
  };

  useEffect(() => {
    if (rowData.length > 0 && selectedUsers.length > 0) {
      const gridApi = gridRef.current?.api;
      if (gridApi) {
        gridApi.forEachNode((node: any) => {
          if (selectedUsers.includes(node.data.userId)) {
            node.setSelected(true);
          }
        });
      }
    }
  }, [rowData, selectedUsers]);

  const onGridReady = (params: any) => {
    params.api.forEachNode((node: any) => {
      if (selectedUsers.includes(node.data.userId)) {
        node.setSelected(true);
      }
    });
  };

  const saveShare = (usersList: any, isShareAction: boolean) => {
    setIsSharing(true);
    dispatch(shareQuerySave({ groupId: selectedGroupId, usersList })).then(
      (response: any) => {
        if (response.meta.requestStatus === "fulfilled") {
          setIsSharing(false);
          dispatch(
            showNotification({
              message: isShareAction
                ? "Changes applied successfully."
                : "Query unshared successfully.",
              duration: 3000,
            }),
          );
          close();

          dispatch(fetchSavedQueries());
        } else {
          setIsSharing(false);
          close();

          dispatch(
            showNotification({
              message: isShareAction
                ? "Query share failed!"
                : "Query unshare failed!",
              duration: 3000,
              isError: true,
            }),
          );
        }
      },
    );
  };

  // if(isLoading){
  //     return <div className="right-panel"><Loading show={true} /></div>;
  // }

  return (
    <div
      className="dialog-container dialog-box-min review-selection"
      style={{ padding: 0, minWidth: 580, maxWidth: 580 }}
    >
      <LoadingOverlay show={isSharing || isLoading} />
      <div
        className="dialog-header"
        style={{
          display: "flex",
          alignItems: "center",
          //   padding: "15px 15px 5px",
          justifyContent: "space-between",
        }}
      >
        <div className="dialog-title">
          <h3>Share / Unshare Query</h3>
        </div>
        <div>
          <SearchInput
            callback={search}
            debounce={300}
            placeholder="Search"
            //   clearIconRight="16%"
          />
        </div>
      </div>

      <div className="dialog-body" style={{ padding: "10px 20px" }}>
        {/* <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div className="search-filter" style={{ width: 200 }}>
         
          </div>
        </div> */}
        <div
          className="ag-theme-alpine share-unshare-list"
          style={{ height: 300 }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              headerClass: "ag-header-left",
            }}
            rowSelection={{
              mode: "multiRow",
              enableClickSelection: false,
            }}
            onSelectionChanged={onSelectionChanged}
            onGridReady={onGridReady}
            suppressContextMenu={true}
            noRowsOverlayComponent={CustomNoRowsOverlay}
            noRowsOverlayComponentParams={noRowsOverlayComponentParams}
          />
        </div>
        {errorMessage && (
          <div
            style={{
              color: "red",
              marginTop: "10px",
              textAlign: "left",
              minHeight: "20px",
            }}
          >
            {errorMessage || "Something went wrong! "}
          </div>
        )}
      </div>

      <div className="dialog-footer" style={{ padding: "15px 20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 10,
            justifyContent: "flex-end",
          }}
        >
          <Button
            className="btn sm"
            color="primary"
            variant="outline"
            id="btn-close"
            onClick={close}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="btn sm"
            variant="outline"
            onClick={() => {
              setShowConfirmPopup(true);

              setConfirmPopUp({
                title: `Confirm Unshare All`,
                message:
                  "Are you sure you want to unshare this query with all users?",

                confirmAction: () => {
                  saveShare([], false);
                  setShowConfirmPopup(false);
                },
                closeAction: () => {
                  setShowConfirmPopup(false);
                },
                id: 0,
              });
            }}
            id="btn-close"
            disabled={prevSelectedUsers.length == 0}
          >
            Unshare All
          </Button>
          <Button
            color="primary"
            className="btn sm"
            variant="solid"
            id="btn-apply"
            disabled={
              isModifyList(selectedUsers, prevSelectedUsers) ||
              (prevSelectedUsers.length == 0 && selectedUsers.length == 0)
            }
            onClick={() => {
              if (!isModifyList(selectedUsers, prevSelectedUsers)) {
                setShowConfirmPopup(true);

                setConfirmPopUp({
                  title: `Share / Unshare Query`,
                  message: "Are you sure you want to make the changes?",

                  confirmAction: () => {
                    saveShare(selectedUsers, true);
                    setShowConfirmPopup(false);
                  },
                  closeAction: () => {
                    setShowConfirmPopup(false);
                  },
                  id: 0,
                });
              } else {
                saveShare(selectedUsers, true);
              }
            }}
            // if (selectedUsers.length > 0) {

            // } else {
            //   setErrorMessage(
            //     "Please select at least one user to share the query",
            //   );
            // }
            // }}
          >
            Apply
          </Button>
        </div>
      </div>
      {showConfirmPopup && (
        <ConfirmPopup
          title={confirmPopUp.title}
          id={confirmPopUp.id}
          message={confirmPopUp.message}
          bodyStyle={{ padding: 0 }}
          confirmAction={confirmPopUp.confirmAction}
          closeAction={confirmPopUp.closeAction}
          footerStyle={{ borderTop: "none" }}
        ></ConfirmPopup>
      )}
    </div>
  );
};

export default ShareQueryPopup;
